import type { PublicBrandingResponse } from '@proerp/shared';

/**
 * Zuletzt geladenes öffentliches Branding (Login-Panel/App-Logo) im localStorage
 * — als Sofort-Fallback fürs nächste Laden. Ohne das zeigt `AuthGate` (und die
 * Sidebar/Topbar) bis zur Antwort von `/v1/branding` das eingebaute Standard-
 * Branding ("P"/ProERP, dunkler Verlauf); bei langsamer Verbindung/Server-
 * Kaltstart wird dieser Zwischenzustand sichtbar — auch wenn längst ein
 * eigenes Branding hinterlegt ist.
 */
const STORAGE_KEY = 'proerp.branding';

/**
 * Grobe Formprüfung statt blindem Vertrauen: `usePublicBranding()` schreibt die
 * rohe API-Antwort ungeprüft hierher — kommt (z. B. durch einen Fehler beim
 * Aufrufer oder eine kaputte Zwischenstation) etwas anderes als ein Branding-
 * Objekt an, würde `Shell`/`BrandMark` beim nächsten Laden auf `loginBullets`
 * o. Ä. crashen. Weder Schreiben noch Lesen vertraut daher ungeprüften Daten.
 */
function isValidBranding(value: unknown): value is PublicBrandingResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Array.isArray((value as PublicBrandingResponse).loginBullets)
  );
}

export function readCachedBranding(): PublicBrandingResponse | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isValidBranding(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeCachedBranding(branding: PublicBrandingResponse): void {
  if (!isValidBranding(branding)) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(branding));
  } catch {
    // Speicher voll/deaktiviert — dann gibt es beim nächsten Laden halt keinen
    // Sofort-Fallback, nicht kritisch.
  }
}
